import React, { ComponentType, lazy, LazyExoticComponent } from 'react';
import { Box, Typography, Alert } from '@mui/material';

const DEFAULT_RETRY_COUNT = 5;
const DEFAULT_RETRY_DELAY = 1000;

/**
 * Заглушка для недоступных модулей
 */
function ModuleUnavailableFallback({ moduleName }: { moduleName: string }) {
  const isProduction = import.meta.env.PROD;
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        p: 3,
      }}
    >
      <Alert severity="warning" sx={{ mb: 2, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom> Модуль недоступен</Typography>
        <Typography variant="body2" paragraph>
          Модуль "{moduleName}" временно недоступен. Пожалуйста, попробуйте позже или обратитесь к администратору.
        </Typography>
        {isProduction && (
          <Typography variant="caption" color="text.secondary">
            Ошибка зарегистрирована и будет исправлена в ближайшее время.
          </Typography>
        )}
      </Alert>
    </Box>
  );
}

/**
 * Проверяет, является ли ошибка временной (сетевой) и стоит ли повторить запрос
 */
function isRetryableError(error: any): boolean {
  const message = error?.message || String(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Unable to preload CSS') ||
    message.includes('Failed to load module script') ||
    message.includes('NetworkError') ||
    message.includes('fetch')
  );
}

/**
 * Задержка перед повторной попыткой
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface SafeLazyImportOptions {
  retryCount?: number;
  retryDelay?: number;
  fallbackComponent?: ComponentType<{ moduleName: string }>;
}

/**
 * Создает безопасный lazy-импорт с обработкой ошибок, retry и заглушкой
 * @param importFn - Функция импорта модуля
 * @param moduleName - Имя модуля для отображения в заглушке
 * @param options - Опции: retryCount, retryDelay, fallbackComponent
 */
export function safeLazyImport<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  moduleName: string,
  options?: SafeLazyImportOptions | ComponentType<{ moduleName: string }>
): LazyExoticComponent<T> {
  const opts: SafeLazyImportOptions = typeof options === 'function' 
    ? { fallbackComponent: options } 
    : options || {};
  
  const retryCount = opts.retryCount ?? DEFAULT_RETRY_COUNT;
  const retryDelay = opts.retryDelay ?? DEFAULT_RETRY_DELAY;
  const Fallback = opts.fallbackComponent || ModuleUnavailableFallback;

  return lazy(async () => {
    let lastError: any;

    for (let attempt = 0; attempt < retryCount; attempt++) {
      try {
        const module = await importFn();
        
        // Проверяем, что модуль загружен корректно
        if (!module || !module.default) {
          throw new Error(`Module ${moduleName} loaded but default export is missing`);
        }

        // Успешная загрузка — очищаем ошибку из реестра
        if (typeof window !== 'undefined') {
          const errorRegistry = (window as any).__mfeLoadErrors__ || {};
          delete errorRegistry[moduleName];
        }

        return module;
      } catch (error: any) {
        lastError = error;
        
        const isRetryable = isRetryableError(error);
        const hasMoreAttempts = attempt < retryCount - 1;

        if (isRetryable && hasMoreAttempts) {
          console.warn(
            `[SafeLazyImport] Failed to load module "${moduleName}" (attempt ${attempt + 1}/${retryCount}). Retrying in ${retryDelay}ms...`,
            error
          );
          await delay(retryDelay);
        } else {
          console.warn(
            `[SafeLazyImport] Failed to load module "${moduleName}" after ${attempt + 1} attempt(s):`,
            error
          );
          break;
        }
      }
    }

    // Регистрируем ошибку в глобальном хранилище для prefetchRemotes
    if (typeof window !== 'undefined') {
      const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
      errorRegistry[moduleName] = {
        error: String(lastError?.message || lastError),
        timestamp: Date.now(),
      };
    }

    // Возвращаем заглушку вместо реального модуля
    return {
      default: ((props: any) => <Fallback moduleName={moduleName} {...props} />) as T,
    };
  });
}

/**
 * Проверяет, был ли модуль загружен с ошибкой
 */
export function isModuleLoadError(moduleName: string): boolean {
  if (typeof window === 'undefined') return false;
  const errorRegistry = (window as any).__mfeLoadErrors__ || {};
  return !!errorRegistry[moduleName];
}

/**
 * Получает информацию об ошибке загрузки модуля
 */
export function getModuleLoadError(moduleName: string): { error: string; timestamp: number } | null {
  if (typeof window === 'undefined') return null;
  const errorRegistry = (window as any).__mfeLoadErrors__ || {};
  return errorRegistry[moduleName] || null;
}

